import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
  SheetClose,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Trash2, Plus, Info } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { formatCurrency } from "@/lib/utils";
import { getPayableReimbursements } from "@/services/payroll.service";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export function PayrollSheet({
  openSheet,
  setOpenSheet,
  selectedEmployee,
  onSavePayroll,
  periodMonth,
  periodYear,
  is_termination = false,
  is_one_time = false,
}) {
  const [baseEarnings, setBaseEarnings] = useState([]);
  const [baseDeductions, setBaseDeductions] = useState([]);
  const [baseStatutory, setBaseStatutory] = useState({
    tds: 0,
    epf_employee: 0,
    esi_employee: 0,
    professional_tax: 0,
    lwf_employee: 0,
  });
  // Whether LOP proration applies to each statutory field. TDS/EPF/ESI are
  // system components → default ON; the admin can turn any of them OFF to enter
  // a flat/literal amount. (PT/LWF are handled separately and never prorated.)
  const STATUTORY_LOP_DEFAULT = { tds: true, epf_employee: true, esi_employee: true };
  const [statutoryApplyLop, setStatutoryApplyLop] = useState(STATUTORY_LOP_DEFAULT);

  const [lopDays, setLopDays] = useState(0);
  const [netPay, setNetPay] = useState(0);
  const [grossPay, setGrossPay] = useState(0);

  const [loanDeductions, setLoanDeductions] = useState([]);
  const [totalDeductions, setTotalDeductions] = useState(0);
  const [reimbursements, setReimbursements] = useState([]);
  // Pool of approved, in-window claims for this employee/period — used to re-add
  // lines the admin removed from the draft.
  const [payablePool, setPayablePool] = useState([]);
  const [showAddList, setShowAddList] = useState(false);
  const [showLopHelp, setShowLopHelp] = useState(false);

  const clampLopDays = (value, maxDays) => {
    const num = Number(value);

    if (isNaN(num) || num < 0) return 0;
    if (num > maxDays) return maxDays;

    return num;
  };

  const periodDays = useMemo(() => {
    if (selectedEmployee?.last_working_date && is_termination) {
      const lwd = new Date(selectedEmployee.last_working_date);
      // When the employee JOINED the same month they are being terminated, the
      // pay period starts at the join date, not the 1st — otherwise a mid-month
      // joiner (e.g. joined 7th, left 25th) shows 25 payable days instead of 19.
      // Mirrors the backend settlement calc so the label matches what is paid.
      const dojRaw =
        selectedEmployee?.employee_data?.date_of_joining ||
        selectedEmployee?.date_of_joining ||
        null;
      const doj = dojRaw ? new Date(dojRaw) : null;
      const startDay =
        doj &&
        !Number.isNaN(doj.getTime()) &&
        doj.getMonth() === lwd.getMonth() &&
        doj.getFullYear() === lwd.getFullYear()
          ? doj.getDate()
          : 1;
      return Math.max(lwd.getDate() - startDay + 1, 0);
    }

    return 30;
  }, [selectedEmployee]);

  const payableDays = Math.max(periodDays - Number(lopDays || 0), 0);

  // Derived pay ratio
  const payRatio = useMemo(() => {
    return periodDays > 0 ? payableDays / periodDays : 0;
  }, [periodDays, payableDays]);

  // Initialize base data
  useEffect(() => {
    console.log("selectedEmployee", selectedEmployee);
    if (openSheet && selectedEmployee) {
      // if (is_one_time) {
      //   // MANUAL MODE
      //   setBaseStatutory({
      //     tds: 0,
      //     epf_employee: 0,
      //     esi_employee: 0,
      //     professional_tax: 0,
      //     lwf_employee: 0,
      //   });
      //   setLoanDeductions([]);
      //   setLopDays(0);
      //   return;
      // }

      // REGULAR / TERMINATION MODE
      // Normalize `applyLop` per line: honour a saved value, else default system
      // components to ON (prorate) and reimbursements to OFF (flat).
      setBaseEarnings(
        (selectedEmployee?.monthly_earnings || selectedEmployee?.earnings || []).map(
          (e) => ({
            ...e,
            applyLop: e.applyLop ?? !isReimbursement(e),
          }),
        ),
      );
      setBaseDeductions(
        (
          selectedEmployee?.monthly_deductions_list ||
          selectedEmployee?.deductions_list ||
          []
        ).map((d) => ({ ...d, applyLop: d.applyLop ?? true })),
      );
      setBaseStatutory({
        tds:
          selectedEmployee?.monthly_deductions?.tds ??
          selectedEmployee?.deductions?.tds ??
          0,
        epf_employee:
          selectedEmployee?.monthly_deductions?.epf_employee ??
          selectedEmployee?.deductions?.epf_employee ??
          0,
        esi_employee:
          selectedEmployee?.monthly_deductions?.esi_employee ??
          selectedEmployee?.deductions?.esi_employee ??
          0,
        professional_tax:
          selectedEmployee?.monthly_deductions?.professional_tax ??
          selectedEmployee?.deductions?.professional_tax ??
          0,
        lwf_employee:
          selectedEmployee?.monthly_deductions?.lwf_employee ??
          selectedEmployee?.deductions?.lwf_employee ??
          0,
      });
      // Fresh dialog: TDS/EPF/ESI default to LOP-prorated; admin can toggle off.
      setStatutoryApplyLop(
        selectedEmployee?.statutory_apply_lop || STATUTORY_LOP_DEFAULT,
      );
      setNetPay(selectedEmployee.net_pay || 0);
      setLopDays(
        is_termination
          ? selectedEmployee?.lop_days ?? 0
          : selectedEmployee?.lop_days ??
              selectedEmployee?.total_unpaid_leaves ??
              0,
      );
      setLoanDeductions(
        (selectedEmployee.loans || []).map((loan) => ({
          ...loan,
          adjustedAmount: Number(loan.monthly_emi || 0), // No LOP applied
        })),
      );
      setReimbursements(selectedEmployee?.reimbursements || []);
      setShowAddList(false);

      if (
        !is_one_time &&
        periodMonth &&
        periodYear &&
        selectedEmployee?.employee_id
      ) {
        getPayableReimbursements(
          selectedEmployee.employee_id,
          periodMonth,
          periodYear,
          is_termination,
        )
          .then((res) => setPayablePool(res?.reimbursements || []))
          .catch(() => setPayablePool([]));
      } else {
        setPayablePool([]);
      }
    }
  }, [openSheet, selectedEmployee, is_one_time, periodMonth, periodYear]);

  // Reimbursements are a fixed payback — never prorated by attendance / LOP.
  const isReimbursement = (e) =>
    (e?.name || "").trim().toLowerCase() === "reimbursement";

  // Adjusted data (computed from base + LOP). Each line carries `applyLop`:
  // when true the amount is prorated (× payRatio), when false it's a flat/literal
  // amount. Reimbursements are always flat regardless of the flag.
  const earnings = baseEarnings.map((e) => ({
    ...e,
    adjustedAmount:
      isReimbursement(e) || !e.applyLop
        ? Number(e.amount || 0)
        : Number((Number(e.amount || 0) * payRatio).toFixed(2)),
  }));

  // Amount docked by LOP = full-month earnings minus the prorated ones. Reimbursement
  // lines aren't prorated, so they cancel out and correctly contribute nothing.
  const lopAmount = Math.max(
    baseEarnings.reduce((sum, e) => sum + Number(e.amount || 0), 0) -
      earnings.reduce((sum, e) => sum + Number(e.adjustedAmount || 0), 0),
    0,
  );

  // Deduction lines prorate by LOP only when `applyLop` is set; otherwise the
  // amount is taken literally (a flat/lump-sum deduction).
  const deductions = baseDeductions.map((d) => ({
    ...d,
    adjustedAmount: d.applyLop
      ? Number((Number(d.amount || 0) * payRatio).toFixed(2))
      : Number(d.amount || 0),
  }));

  // Statutory: TDS/EPF/ESI prorate by LOP when their `statutoryApplyLop` flag is
  // on (the default for these system components); flipping it off makes the
  // entered value literal. PT/LWF are never LOP-prorated.
  const statutoryValue = (key) =>
    statutoryApplyLop[key]
      ? Number((Number(baseStatutory[key] || 0) * payRatio).toFixed(2))
      : Number(Number(baseStatutory[key] || 0).toFixed(2));
  const statutory = {
    tds: statutoryValue("tds"),
    epf_employee: statutoryValue("epf_employee"),
    esi_employee: statutoryValue("esi_employee"),
    professional_tax: Number(Number(baseStatutory.professional_tax || 0).toFixed(2)),
    lwf_employee: Number(Number(baseStatutory.lwf_employee || 0).toFixed(2)),
  };

  // Calculate total & net pay
  useEffect(() => {
    const totalEarnings = earnings.reduce(
      (sum, e) => sum + Number(e.adjustedAmount || 0),
      0,
    );

    const totalDeductions =
      deductions.reduce((sum, d) => sum + Number(d.adjustedAmount || 0), 0) +
      Object.values(statutory).reduce((sum, val) => sum + Number(val || 0), 0) +
      loanDeductions.reduce(
        (sum, loan) => sum + Number(loan.adjustedAmount || 0),
        0,
      );

    // Reimbursements are added AFTER deductions (non-taxable), never prorated.
    const reimbursementsTotal = reimbursements.reduce(
      (sum, r) => sum + Number(r.amount || 0),
      0,
    );

    setTotalDeductions(totalDeductions);
    setNetPay(totalEarnings - totalDeductions + reimbursementsTotal);
    setGrossPay(totalEarnings);
  }, [earnings, deductions, statutory, reimbursements]);

  // --- Handlers ---
  // Manually added lines default to a FLAT amount (applyLop: false) — a
  // lump-sum adjustment. The admin can tick "Apply LOP" to prorate it instead.
  const handleAddEarning = () =>
    setBaseEarnings([...baseEarnings, { name: "", amount: 0, applyLop: false }]);
  const handleAddDeduction = () =>
    setBaseDeductions([...baseDeductions, { name: "", amount: 0, applyLop: false }]);

  const handleChange = (type, index, key, value) => {
    const update = type === "earning" ? [...baseEarnings] : [...baseDeductions];
    update[index][key] = value;
    type === "earning" ? setBaseEarnings(update) : setBaseDeductions(update);
  };

  // Toggle whether a line's amount is LOP-prorated or taken flat/literal.
  const handleToggleLop = (type, index, checked) => {
    const update = type === "earning" ? [...baseEarnings] : [...baseDeductions];
    update[index].applyLop = checked;
    type === "earning" ? setBaseEarnings(update) : setBaseDeductions(update);
  };

  const handleToggleStatutoryLop = (field, checked) =>
    setStatutoryApplyLop((prev) => ({ ...prev, [field]: checked }));

  const handleDelete = (type, index) => {
    const update = type === "earning" ? [...baseEarnings] : [...baseDeductions];
    update.splice(index, 1);
    type === "earning" ? setBaseEarnings(update) : setBaseDeductions(update);
  };

  const handleStatutoryChange = (field, value) =>
    setBaseStatutory((prev) => ({ ...prev, [field]: Number(value) || 0 }));

  // Reimbursement amounts are fixed (approved) — only deletion is allowed.
  // Deleting defers the claim: it stays "approved" and rolls into the next run.
  const handleDeleteReimbursement = (index) =>
    setReimbursements((prev) => prev.filter((_, i) => i !== index));

  // Re-include a previously-removed approved claim. Amount stays the approved
  // (read-only) value; Save persists it and Lock finalizes it.
  const handleAddReimbursement = (claim) =>
    setReimbursements((prev) => [
      ...prev,
      {
        claim_id: claim.claim_id,
        claim_type: claim.claim_type,
        amount: Number(claim.amount || 0),
      },
    ]);

  const handleSave = () => {
    const updated = {
      ...selectedEmployee,
      lop_days: lopDays,
      // Keep the fields the run table reads in sync with the edited LOP, so the
      // "Unpaid Days" column (and payable days) reflect a manual change on save.
      total_unpaid_leaves: Number(lopDays) || 0,
      total_working_days: payableDays,
      pay_ratio: payRatio,
      // Persist the per-key statutory LOP choices so they round-trip on re-open.
      statutory_apply_lop: statutoryApplyLop,
      earnings: baseEarnings.map((e) => ({
        name: e.name,
        amount: Number(e.amount || 0),
        applyLop: !isReimbursement(e) && !!e.applyLop,
        adjustedAmount:
          isReimbursement(e) || !e.applyLop
            ? Number(e.amount || 0)
            : Number((Number(e.amount || 0) * payRatio).toFixed(2)),
      })),

      deductions_list: baseDeductions.map((d) => ({
        name: d.name,
        amount: Number(d.amount || 0),
        applyLop: !!d.applyLop,
        // Prorated only when the line's Apply LOP is on; else literal.
        adjustedAmount: d.applyLop
          ? Number((Number(d.amount || 0) * payRatio).toFixed(2))
          : Number(d.amount || 0),
      })),

      deductions: {
        ...baseStatutory,
        ...statutory,
        loan_emi: loanDeductions.reduce(
          (sum, loan) => sum + Number(loan.adjustedAmount || 0),
          0,
        ),
      },

      // Full deductions total — statutory + custom deduction lines + loan EMI —
      // matching the backend convention and net_pay. (Was statutory-only, which
      // dropped Loan EMI from the payslip's "Total Deductions".)
      total_deductions: totalDeductions,

      loans: loanDeductions,
      loan_deductions: loanDeductions.reduce(
        (sum, loan) => sum + Number(loan.adjustedAmount || 0),
        0,
      ),
      net_pay: netPay,
      gross_pay: grossPay,
      // Only the surviving claims — deleted ones are omitted, so they stay
      // "approved" and roll into the next payroll run.
      reimbursements: reimbursements.map((r) => ({
        claim_id: r.claim_id,
        claim_type: r.claim_type,
        amount: Number(r.amount || 0),
      })),
    };

    // Notify parent to update the table
    if (onSavePayroll) onSavePayroll(updated);

    // Close the drawer
    setOpenSheet(false);
  };

  // Approved claims eligible for this period but not currently in the draft
  // (i.e. ones the admin removed) — these are what the "Add" button offers.
  const addableClaims = payablePool.filter(
    (p) =>
      !reimbursements.some((r) => String(r.claim_id) === String(p.claim_id)),
  );

  return (
    <Sheet open={openSheet} onOpenChange={setOpenSheet}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md md:max-w-lg lg:max-w-2xl h-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 shadow-2xl p-0"
      >
        {/* Header */}
        <SheetHeader className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <SheetTitle className="text-xl font-semibold">
            Edit Payroll: {selectedEmployee?.name || "Employee"} (
            {formatCurrency(
              selectedEmployee?.monthly_gross_pay ?? selectedEmployee?.gross_pay,
            )}
            )
          </SheetTitle>
          <SheetDescription>
            Emp ID: {selectedEmployee?.empId || "--"}
          </SheetDescription>
        </SheetHeader>

        {/* Body */}
        <ScrollArea className="h-[calc(100vh-150px)] px-6 py-4">
          {/* --- LOP Section --- */}
          {!is_one_time && (
            <>
              <section className="mb-6">
                <div className="flex items-center gap-1.5 mb-2">
                  <h3 className="text-sm font-semibold text-amber-700 dark:text-amber-400">
                    LOSS OF PAY (LOP)
                  </h3>
                  <button
                    type="button"
                    onClick={() => setShowLopHelp(true)}
                    className="text-muted-foreground hover:text-amber-700 dark:hover:text-amber-400 transition-colors"
                    title="How LOP & the 'Apply LOP' checkboxes work"
                    aria-label="How LOP and the Apply LOP checkboxes work"
                  >
                    <Info className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <Input
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    value={lopDays}
                    min={0}
                    max={periodDays}
                    placeholder="Enter LOP days"
                    className="w-24 text-center"
                    onChange={(e) => {
                      const safeValue = clampLopDays(
                        e.target.value,
                        periodDays,
                      );
                      setLopDays(safeValue);
                    }}
                    onBlur={(e) => {
                      const value = Number(e.target.value);
                      if (value > periodDays) setLopDays(periodDays);
                      if (value < 0) setLopDays(0);
                    }}
                  />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Payable Days:{" "}
                    <span className="font-semibold text-gray-800 dark:text-gray-200">
                      {payableDays} / {periodDays}
                    </span>{" "}
                    ({(payRatio * 100).toFixed(1)}%)
                  </p>
                </div>
              </section>

              <Separator className="my-4" />
            </>
          )}

          {/* --- Earnings --- */}
          <section className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                EARNINGS
              </h3>
              <Button variant="ghost" size="sm" onClick={handleAddEarning}>
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>

            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center gap-2 px-1 pb-2 mb-2 border-b border-border text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                <span className="flex-1">Component</span>
                <span className="w-28 text-right">Amount</span>
                <span
                  className="w-10 text-center"
                  title="Tick to prorate this line by Loss of Pay"
                >
                  LOP
                </span>
                <span className="w-9 shrink-0" aria-hidden="true" />
              </div>
              {earnings.map((item, i) => (
                <div key={i} className="mb-2">
                  <div className="flex items-center gap-2">
                    <Input
                      value={item.name}
                      onChange={(e) =>
                        handleChange("earning", i, "name", e.target.value)
                      }
                      placeholder="Component Name"
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      value={item.amount}
                      onChange={(e) =>
                        handleChange("earning", i, "amount", e.target.value)
                      }
                      placeholder="₹"
                      className="w-28 text-right font-semibold bg-background text-foreground border-input"
                    />
                    <div
                      className="w-10 flex justify-center shrink-0"
                      title="Prorate this line by Loss of Pay"
                    >
                      {!isReimbursement(item) && (
                        <Checkbox
                          checked={!!item.applyLop}
                          onCheckedChange={(v) =>
                            handleToggleLop("earning", i, v === true)
                          }
                        />
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="shrink-0"
                      onClick={() => handleDelete("earning", i)}
                    >
                      <Trash2 className="w-4 h-4 text-gray-500" />
                    </Button>
                  </div>
                  {/* Prorated only when this line has Apply LOP on. Shows what the
                      full-month amount above actually prorates to, and why. */}
                  {item.applyLop && Number(lopDays) > 0 && Number(item.amount || 0) > 0 && (
                    <div className="pr-[5.75rem] text-right text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                      = ₹
                      {(Number(item.amount || 0) * payRatio).toLocaleString(
                        "en-IN",
                        { maximumFractionDigits: 2 },
                      )}{" "}
                      <span className="text-muted-foreground">
                        (₹{Number(item.amount || 0).toLocaleString("en-IN")} ×{" "}
                        {payableDays}/{periodDays} payable days)
                      </span>
                    </div>
                  )}
                </div>
              ))}
              {/* Full-month figures — the LOP shortfall is shown as a deduction below,
                  matching how the payslip presents it. */}
              <div className="text-right font-semibold text-gray-700 dark:text-gray-300 mt-3">
                Gross Earnings: ₹
                {earnings
                  .reduce((sum, e) => sum + Number(e.amount || 0), 0)
                  .toLocaleString("en-IN", { maximumFractionDigits: 2 })}
              </div>
              {Number(lopDays) > 0 && (
                <div className="text-right text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                  Payable ({payableDays}/{periodDays} days): ₹
                  {(
                    earnings.reduce((sum, e) => sum + Number(e.amount || 0), 0) *
                    payRatio
                  ).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                </div>
              )}
            </div>
          </section>

          {/* --- Reimbursements (read-only amounts; delete + re-add from approved pool) --- */}
          {(reimbursements.length > 0 || addableClaims.length > 0) && (
            <section className="mb-8">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-sm font-semibold text-blue-700 dark:text-blue-400">
                  REIMBURSEMENTS
                </h3>
                {addableClaims.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAddList((v) => !v)}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                )}
              </div>

              <div className="rounded-lg border border-blue-200 dark:border-blue-900/40 p-4">
                {reimbursements.length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                    No reimbursements included. Use “Add” to include approved
                    claims.
                  </p>
                )}

                {reimbursements.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 mb-2">
                    <Input
                      value={item.claim_type || "Reimbursement"}
                      readOnly
                      className="flex-1 bg-background text-foreground cursor-default capitalize"
                    />
                    <Input
                      type="text"
                      value={formatCurrency(Number(item.amount || 0))}
                      readOnly
                      className="w-28 text-right font-semibold bg-background text-foreground cursor-default"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteReimbursement(i)}
                    >
                      <Trash2 className="w-4 h-4 text-gray-500" />
                    </Button>
                  </div>
                ))}

                {/* Approved claims available to re-add */}
                {showAddList && addableClaims.length > 0 && (
                  <div className="mt-2 mb-3 rounded-md border border-dashed border-blue-300 dark:border-blue-800 p-3 space-y-2">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      Approved claims available to add
                    </p>
                    {addableClaims.map((claim) => (
                      <div
                        key={String(claim.claim_id)}
                        className="flex items-center justify-between gap-2"
                      >
                        <span className="text-sm capitalize text-gray-800 dark:text-gray-200">
                          {claim.claim_type || "Reimbursement"}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold">
                            {formatCurrency(Number(claim.amount || 0))}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleAddReimbursement(claim)}
                          >
                            <Plus className="w-4 h-4 text-blue-600" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="text-right font-semibold text-gray-700 dark:text-gray-300 mt-3">
                  Total Reimbursements: ₹
                  {reimbursements
                    .reduce((sum, r) => sum + Number(r.amount || 0), 0)
                    .toLocaleString("en-IN")}
                </div>
              </div>
            </section>
          )}

          {/* --- Deductions --- */}
          <section>
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-semibold text-red-600 dark:text-red-400">
                DEDUCTIONS
              </h3>
              <Button variant="ghost" size="sm" onClick={handleAddDeduction}>
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>

            <div className="rounded-lg border border-destructive/20 p-4">
              <div className="flex items-center gap-2 px-1 pb-2 mb-2 border-b border-border text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                <span className="flex-1">Component</span>
                <span className="w-28 text-right">Amount</span>
                <span
                  className="w-10 text-center"
                  title="Tick to prorate this line by Loss of Pay"
                >
                  LOP
                </span>
                <span className="w-9 shrink-0" aria-hidden="true" />
              </div>
              {/* Loss of Pay — derived from the LOP days above, so it's read-only here. */}
              {lopAmount > 0 && (
                <div className="flex items-center gap-2 mb-2">
                  <Input
                    value={`Loss of Pay (${lopDays} ${
                      Number(lopDays) === 1 ? "day" : "days"
                    })`}
                    readOnly
                    className="flex-1 bg-background text-foreground font-medium cursor-default"
                  />
                  <Input
                    type="text"
                    value={lopAmount.toLocaleString("en-IN", {
                      maximumFractionDigits: 2,
                    })}
                    readOnly
                    className="w-28 text-right bg-background text-foreground font-semibold cursor-default"
                  />
                  <span className="w-10 shrink-0" aria-hidden="true" />
                  <span className="w-9 shrink-0" aria-hidden="true" />
                </div>
              )}

              {deductions.map((item, i) => (
                <div key={i} className="mb-2">
                  <div className="flex items-center gap-2">
                    <Input
                      value={item.name}
                      onChange={(e) =>
                        handleChange("deduction", i, "name", e.target.value)
                      }
                      placeholder="Component Name"
                      className="flex-1 bg-background text-foreground"
                    />
                    {/* Bound to the BASE amount the admin types (so 1 shows 1).
                        Whether it's prorated is controlled by the LOP checkbox,
                        not by editing — see the `deductions` memo. */}
                    <Input
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      value={item.amount}
                      onChange={(e) =>
                        handleChange("deduction", i, "amount", e.target.value)
                      }
                      placeholder="₹"
                      className="w-28 text-right bg-background text-foreground"
                    />
                    <div
                      className="w-10 flex justify-center shrink-0"
                      title="Prorate this line by Loss of Pay"
                    >
                      <Checkbox
                        checked={!!item.applyLop}
                        onCheckedChange={(v) =>
                          handleToggleLop("deduction", i, v === true)
                        }
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="shrink-0"
                      onClick={() => handleDelete("deduction", i)}
                    >
                      <Trash2 className="w-4 h-4 text-gray-500" />
                    </Button>
                  </div>
                  {item.applyLop && Number(lopDays) > 0 && Number(item.amount || 0) > 0 && (
                    <div className="pr-[5.75rem] text-right text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                      = ₹
                      {(Number(item.amount || 0) * payRatio).toLocaleString("en-IN", {
                        maximumFractionDigits: 2,
                      })}{" "}
                      <span className="text-muted-foreground">
                        (× {payableDays}/{periodDays} payable days)
                      </span>
                    </div>
                  )}
                </div>
              ))}

              {!is_one_time && (
                <>
                  <Separator className="my-3" />
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Statutory Deductions
                  </h4>

                  {[
                    { label: "TDS", key: "tds" },
                    { label: "EPF (Employee)", key: "epf_employee" },
                    { label: "ESI (Employee)", key: "esi_employee" },
                    // Show Professional Tax whenever the work state is known —
                    // even when it's ₹0 (e.g. a female employee below Maharashtra's
                    // ₹25,000 exemption), so it's clear PT was applied, not omitted.
                    ...(selectedEmployee?.state
                      ? [
                          {
                            label: `${selectedEmployee.state} Professional Tax`,
                            key: "professional_tax",
                          },
                        ]
                      : []),
                    ...(Number(statutory.lwf_employee) > 0
                      ? [
                          {
                            label: `${selectedEmployee?.state} Labour Welfare Fund`,
                            key: "lwf_employee",
                          },
                        ]
                      : []),
                  ].map(({ label, key }) => {
                    // TDS/EPF/ESI can be LOP-prorated (toggle); PT/LWF never are.
                    const proratable = [
                      "tds",
                      "epf_employee",
                      "esi_employee",
                    ].includes(key);
                    return (
                      <div key={key} className="mb-2">
                        <div className="flex items-center gap-2">
                          <Input
                            value={label}
                            readOnly
                            className="flex-1 bg-background text-foreground font-medium cursor-default"
                          />
                          {/* Bound to the BASE value; the LOP checkbox decides
                              whether it's prorated. */}
                          <Input
                            type="number"
                            min={0}
                            value={baseStatutory[key]}
                            onChange={(e) =>
                              handleStatutoryChange(key, e.target.value)
                            }
                            className="w-28 text-right bg-background text-foreground font-semibold"
                          />
                          {proratable ? (
                            <div
                              className="w-10 flex justify-center shrink-0"
                              title="Prorate this component by Loss of Pay"
                            >
                              <Checkbox
                                checked={!!statutoryApplyLop[key]}
                                onCheckedChange={(v) =>
                                  handleToggleStatutoryLop(key, v === true)
                                }
                              />
                            </div>
                          ) : (
                            <span className="w-10 shrink-0" aria-hidden="true" />
                          )}
                          {/* Align with the delete-button column above. */}
                          <span className="w-9 shrink-0" aria-hidden="true" />
                        </div>
                        {proratable &&
                          statutoryApplyLop[key] &&
                          Number(lopDays) > 0 &&
                          Number(baseStatutory[key] || 0) > 0 && (
                            <div className="pr-[5.75rem] text-right text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                              = ₹
                              {(Number(baseStatutory[key] || 0) * payRatio).toLocaleString(
                                "en-IN",
                                { maximumFractionDigits: 2 },
                              )}{" "}
                              <span className="text-muted-foreground">
                                (× {payableDays}/{periodDays} payable days)
                              </span>
                            </div>
                          )}
                      </div>
                    );
                  })}
                </>
              )}

              {/* --- Loan EMI Deductions --- */}
              {!is_one_time && loanDeductions.length > 0 && (
                <>
                  <Separator className="my-3" />
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Loan EMI Deductions
                  </h4>

                  {loanDeductions.map((loan, i) => (
                    <div key={i} className="flex items-center gap-2 mb-2">
                      <Input
                        value={`${loan.type} (EMI)`}
                        readOnly
                        onChange={(e) =>
                          handleStatutoryChange(key, e.target.value)
                        }
                        className="flex-1 bg-background text-foreground"
                      />
                      <Input
                        type="number"
                        value={loan.adjustedAmount}
                        onChange={(e) => {
                          const update = [...loanDeductions];
                          update[i].adjustedAmount =
                            Number(e.target.value) || 0;
                          setLoanDeductions(update);
                        }}
                        className="w-28 text-right bg-background text-foreground"
                      />
                    </div>
                  ))}
                </>
              )}

              {/* Includes the LOP row above, so Gross Earnings − Total Deductions
                  reconciles with the Net Pay below. */}
              <div className="text-right font-semibold text-red-600 dark:text-red-400 mt-3">
                Total Deductions: -₹
                {(totalDeductions + lopAmount).toLocaleString("en-IN", {
                  maximumFractionDigits: 2,
                })}
              </div>
            </div>
          </section>

          <Separator className="my-4" />

          {/* --- Net Pay Summary --- */}
          <div className="flex justify-between items-center">
            <p className="text-lg font-semibold text-gray-800 dark:text-gray-200">
              NET PAY
            </p>
            <p className="text-xl font-bold text-primary">
              ₹{netPay.toLocaleString("en-IN")}
            </p>
          </div>
        </ScrollArea>

        {/* Footer */}
        <SheetFooter className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end gap-3">
          <SheetClose asChild>
            <Button variant="outline">Cancel</Button>
          </SheetClose>
          <Button
            onClick={handleSave}
            className="bg-primary hover:brightness-110 text-white"
          >
            Save Changes
          </Button>
        </SheetFooter>

        {/* How-it-works help for LOP + the per-line "Apply LOP" checkboxes. */}
        <Dialog open={showLopHelp} onOpenChange={setShowLopHelp}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Info className="w-5 h-5 text-amber-600" />
                How Loss of Pay (LOP) works
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 text-sm text-muted-foreground">
              <p>
                <span className="font-medium text-foreground">LOP days</span> are
                the unpaid days in this period.{" "}
                <span className="font-medium text-foreground">Payable days</span>{" "}
                = period days − LOP days, and every prorated amount is scaled by{" "}
                <span className="font-medium text-foreground">
                  payable ÷ period days
                </span>
                .
              </p>

              <div className="rounded-lg border border-border p-3 space-y-3">
                <p className="font-medium text-foreground">
                  The “LOP” checkbox on each line
                </p>
                <div className="flex gap-2">
                  <span className="mt-0.5">☑️</span>
                  <p>
                    <span className="font-medium text-foreground">Ticked</span> —
                    enter the{" "}
                    <span className="font-medium text-foreground">
                      full monthly amount
                    </span>
                    . It's automatically{" "}
                    <span className="font-medium text-foreground">reduced for LOP</span>{" "}
                    (× payable ÷ period days) — so the amount box is the monthly
                    figure, and the amber line shows what actually gets paid. Use
                    for pay that scales with attendance — Basic, HRA, and
                    statutory like EPF/ESI/TDS.
                  </p>
                </div>
                <div className="flex gap-2">
                  <span className="mt-0.5">⬜</span>
                  <p>
                    <span className="font-medium text-foreground">Unticked</span> —
                    the amount is used{" "}
                    <span className="font-medium text-foreground">exactly as typed</span>{" "}
                    (a flat/lump-sum figure), no matter how many LOP days there
                    are. Use for one-off adjustments, fixed bonuses, or fixed
                    deductions.
                  </p>
                </div>
              </div>

              <p>
                The amount box always shows the{" "}
                <span className="font-medium text-foreground">full amount you enter</span>.
                When “LOP” is ticked, a small amber line under it shows what that
                becomes after LOP (e.g. <span className="whitespace-nowrap">“= ₹X × payable/period”</span>).
              </p>

              <p className="text-xs">
                <span className="font-medium text-foreground">Defaults:</span>{" "}
                system components (Basic, HRA, TDS, EPF, ESI) start with LOP{" "}
                <span className="font-medium text-foreground">on</span>; lines you
                add start <span className="font-medium text-foreground">off</span>{" "}
                (flat). Professional Tax, LWF and reimbursements are never
                LOP-adjusted.
              </p>
            </div>

            <DialogFooter>
              <Button onClick={() => setShowLopHelp(false)}>Got it</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
}
